import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Filter, MapPin, TrendingUp } from 'lucide-react';
import { formatCurrency, getStageColor, getStatusColor } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DealsPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    sector: '',
    country: '',
    stage: '',
    search: ''
  });

  useEffect(() => {
    fetchDeals();
  }, [filters]);

  const fetchDeals = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.sector) params.append('sector', filters.sector);
      if (filters.country) params.append('country', filters.country);
      if (filters.stage) params.append('stage', filters.stage);
      
      const response = await axios.get(`${API}/deals?${params}`, {
        headers: getAuthHeaders()
      });
      
      let filteredDeals = response.data;
      if (filters.search) {
        filteredDeals = filteredDeals.filter(deal => 
          deal.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          deal.developer.toLowerCase().includes(filters.search.toLowerCase())
        );
      }
      
      setDeals(filteredDeals);
    } catch (error) {
      console.error('Failed to fetch deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const sectors = ['Energy / Oil & Gas', 'Minerals', 'Other Industrial'];
  const countries = ['Algeria', 'Angola', 'Bahrain', 'Benin', 'Botswana', 'Burkina Faso', 'Burundi', 'Cameroon', 'Cape Verde', 'Central African Republic', 'Chad', 'Comoros', 'Cote d\'Ivoire', 'Djibouti', 'DRC', 'Egypt', 'Equatorial Guinea', 'Eritrea', 'Eswatini', 'Ethiopia', 'Gabon', 'Gambia', 'Ghana', 'Guinea', 'Guinea-Bissau', 'Iraq', 'Iran', 'Israel', 'Jordan', 'Kenya', 'Kuwait', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mauritius', 'Morocco', 'Mozambique', 'Namibia', 'Niger', 'Nigeria', 'Oman', 'Palestine', 'Qatar', 'Republic of Congo', 'Rwanda', 'Saudi Arabia', 'Senegal', 'Seychelles', 'Sierra Leone', 'Somalia', 'South Africa', 'South Sudan', 'Sudan', 'Syria', 'Tanzania', 'Togo', 'Tunisia', 'Turkey', 'UAE', 'Uganda', 'Yemen', 'Zambia', 'Zimbabwe'];
  const stages = ['concept', 'feasibility', 'development', 'financial_close', 'construction', 'operational'];

  return (
    <div className="p-6 space-y-6" data-testid="deals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Deal Directory</h1>
          <p className="text-muted-foreground">Browse infrastructure investment opportunities</p>
        </div>
        <Button onClick={() => navigate('/deals/submit', { state: { dealType: 'infrastructure' } })} data-testid="submit-deal-button">
          Submit Infrastructure Deal
        </Button>
      </div>

      <Card data-testid="filters-card">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                placeholder="Search deals..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={filters.sector} onValueChange={(value) => setFilters({ ...filters, sector: value })}>
              <SelectTrigger data-testid="sector-filter">
                <SelectValue placeholder="All Sectors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                {sectors.map(sector => (
                  <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.country} onValueChange={(value) => setFilters({ ...filters, country: value })}>
              <SelectTrigger data-testid="country-filter">
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.stage} onValueChange={(value) => setFilters({ ...filters, stage: value })}>
              <SelectTrigger data-testid="stage-filter">
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {stages.map(stage => (
                  <SelectItem key={stage} value={stage} className="capitalize">{stage}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={() => setFilters({ sector: '', country: '', stage: '', search: '' })}
              data-testid="clear-filters-button"
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading deals...</div>
        </div>
      ) : deals.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              No deals found matching your criteria
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {deals.map((deal) => (
            <Card
              key={deal.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/deals/${deal.id}`)}
              data-testid={`deal-card-${deal.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">{deal.name}</CardTitle>
                      {deal.verification_status === 'verified' && <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-[9px] px-1.5">Verified</Badge>}
                      {deal.listing_type === 'demo' && <Badge variant="outline" className="text-[9px] px-1.5 border-orange-300 text-orange-600">Demo</Badge>}
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <MapPin size={14} />
                      <span>{deal.country}</span>
                      <span>&middot;</span>
                      <span>{deal.sector}</span>
                    </div>
                  </div>
                  <Badge className={getStatusColor(deal.status)} data-testid={`status-badge-${deal.id}`}>
                    {deal.status?.replace(/_/g, ' ')}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-4 py-3 border-t border-b border-border">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Capital Required</p>
                    <p className="text-sm font-mono font-semibold mt-1">{formatCurrency(deal.capital_required)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Target IRR</p>
                    <p className="text-sm font-mono font-semibold mt-1">{deal.target_irr ? `${deal.target_irr}%` : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Stage</p>
                    <Badge className={`${getStageColor(deal.stage)} mt-1`}>
                      {deal.stage}
                    </Badge>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground line-clamp-2">{deal.summary}</p>
                
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
                      Equity: {deal.equity_split}%
                    </span>
                    <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
                      Debt: {deal.debt_split}%
                    </span>
                  </div>
                  {deal.score && (
                    <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                      <TrendingUp size={12} />
                      <span className="font-mono">{deal.score.toFixed(1)}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
